import { useMemo } from 'react';
import { Check, Lock, Sparkles, Info, Wrench, Monitor, Layers } from 'lucide-react';
import {
  ALL_LICENSE_PRICE_USD,
  HMI_LICENSE_PRICE_USD,
  HMI_SUPPORT_YEARLY_USD,
  HMI_UPGRADE_CREDIT_USD,
  LICENSABLE_FEATURES,
  LICENSE_BASE_FEATURES,
  LICENSE_DEPENDENCIES,
  LICENSE_FEATURE_PRICES_USD,
  LICENSE_GROUPS,
  LICENSE_PRESETS,
  MAINTENANCE_RATE,
  quoteLicense,
  withDependencies,
  type LicensableFeature,
  type LicenseEdition,
  type LicenseQuote,
  FEATURE_METADATA,
} from '@scada-ink/shared/cloud';

/**
 * The feature picker shared by the public builder (/license-builder) and the
 * portal purchase page. It only edits a selection and renders a quote; where
 * the quote comes from (local preview or the cloud) is the caller's business.
 */

export interface LicenseSelection {
  /** scada: pick features or All. hmi: the flat HMI.ink package. */
  edition: LicenseEdition;
  all: boolean;
  features: LicensableFeature[];
  maintenance: boolean;
}

export const EMPTY_SELECTION: LicenseSelection = { edition: 'scada', all: false, features: [], maintenance: true };

export const formatUsd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

const LIST_TOTAL_USD = LICENSABLE_FEATURES.reduce((s, f) => s + LICENSE_FEATURE_PRICES_USD[f], 0);

/** Selection ⇄ URL, so a public selection survives sign-up and lands in the portal intact. */
export function selectionToParams(sel: LicenseSelection, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams(extra);
  if (sel.edition === 'hmi') p.set('package', 'hmi');
  else if (sel.all) p.set('all', '1');
  else if (sel.features.length) p.set('features', sel.features.join(','));
  p.set('maintenance', sel.maintenance ? '1' : '0');
  return p;
}

export function selectionFromParams(p: URLSearchParams): LicenseSelection {
  return {
    // HMI.ink links arrive as ?package=hmi (from hmi.ink and the pricing page).
    edition: p.get('package') === 'hmi' ? 'hmi' : 'scada',
    all: p.get('all') === '1',
    features: withDependencies((p.get('features') ?? '').split(',').filter(Boolean)),
    maintenance: p.get('maintenance') !== '0',
  };
}

/** Which selected features pull each dependency in. */
function requiredBy(selected: Set<string>): Map<LicensableFeature, LicensableFeature[]> {
  const out = new Map<LicensableFeature, LicensableFeature[]>();
  for (const f of selected) {
    for (const dep of LICENSE_DEPENDENCIES[f as LicensableFeature] ?? []) {
      if (!out.has(dep)) out.set(dep, []);
      out.get(dep)!.push(f as LicensableFeature);
    }
  }
  return out;
}

export function FeaturePicker({
  selection,
  onChange,
  owned = [],
  disabled = false,
}: {
  selection: LicenseSelection;
  onChange: (next: LicenseSelection) => void;
  owned?: LicensableFeature[];
  disabled?: boolean;
}) {
  const ownedSet = useMemo(() => new Set(owned), [owned]);
  const chosen = useMemo(() => new Set(selection.features), [selection.features]);
  const required = useMemo(() => requiredBy(new Set([...chosen, ...ownedSet])), [chosen, ownedSet]);
  const ownsAll = owned.length === LICENSABLE_FEATURES.length;

  const toggle = (f: LicensableFeature) => {
    const next = new Set(chosen);
    if (next.has(f)) next.delete(f);
    else next.add(f);
    onChange({ ...selection, all: false, features: withDependencies(next) });
  };

  const applyPreset = (features: LicensableFeature[]) => onChange({ ...selection, all: false, features: withDependencies(features) });

  if (selection.edition === 'hmi') {
    return (
      <div className="space-y-6">
        <PackagePicker selection={selection} onChange={onChange} disabled={disabled} />
        <div className="glass-panel p-6">
          <img src="/hmi-ink-logo.png" alt="HMI.ink" className="h-12 w-auto" />
          <p className="mt-4 text-sm text-text-secondary leading-relaxed">
            A focused industrial display license: connect to machines, browse tags and build operator screens with controls. One flat price per appliance.
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 text-sm">
            {['Network Scanner & device connections', 'Tag Browser with live values', 'Drag-and-drop dashboards', 'Buttons, toggles, sliders & inputs', 'Custom HTML screens', 'Kiosk & TV display modes'].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check size={15} className="text-status-running mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-text-muted">
            Not included: historian, alarms, workflows & scripting, reporting, MES, API and AI. Upgrade to Scada.Ink any time — your {formatUsd(HMI_UPGRADE_CREDIT_USD)} HMI.ink license is credited.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PackagePicker selection={selection} onChange={onChange} disabled={disabled} />
      {/* The All license */}
      <button
        type="button"
        disabled={disabled || ownsAll}
        onClick={() => onChange({ ...selection, all: !selection.all, features: selection.all ? [] : selection.features })}
        className={`w-full text-left relative overflow-hidden rounded-[var(--radius-glass)] border p-5 sm:p-6 transition-all ${
          selection.all || ownsAll ? 'bg-navy-900 border-navy-900 text-white ring-4 ring-brand-primary/20' : 'bg-white border-glass-border hover:border-brand-primary/40'
        }`}
        aria-pressed={selection.all}
      >
        {(selection.all || ownsAll) && <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-secondary/35 blur-3xl" />}
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <span
            className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${
              selection.all || ownsAll ? 'bg-brand-accent border-brand-accent text-navy-900' : 'border-glass-border-bright'
            }`}
          >
            {(selection.all || ownsAll) && <Check size={16} strokeWidth={3} />}
          </span>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-bold">All features</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${selection.all || ownsAll ? 'bg-white/15 text-brand-accent' : 'bg-status-running/10 text-status-running'}`}>
                Save {formatUsd(LIST_TOTAL_USD - ALL_LICENSE_PRICE_USD)}
              </span>
            </div>
            <p className={`mt-1 text-sm ${selection.all || ownsAll ? 'text-slate-300' : 'text-text-secondary'}`}>
              Every licensed capability — production analytics, alarms & reporting, MES, control, connectivity and more.
            </p>
          </div>
          <div className="sm:text-right">
            <div className="text-2xl font-extrabold">{formatUsd(ALL_LICENSE_PRICE_USD)}</div>
            <div className={`text-xs ${selection.all || ownsAll ? 'text-slate-400' : 'text-text-muted'}`}>
              <span className="line-through">{formatUsd(LIST_TOTAL_USD)}</span> one-time
            </div>
          </div>
        </div>
      </button>

      {/* Presets */}
      {!selection.all && !ownsAll && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-text-muted">Start from:</span>
          {LICENSE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={disabled}
              onClick={() => applyPreset(p.features)}
              className="px-3 py-1.5 rounded-full border border-glass-border-bright bg-white text-xs font-semibold hover:border-brand-primary/40 hover:text-brand-primary transition-colors"
            >
              {p.name}
            </button>
          ))}
          {selection.features.length > 0 && (
            <button type="button" disabled={disabled} onClick={() => applyPreset([])} className="px-3 py-1.5 text-xs font-semibold text-text-muted hover:text-status-fault">
              Clear
            </button>
          )}
        </div>
      )}

      {/* Feature groups */}
      <div className="grid md:grid-cols-2 gap-4">
        {LICENSE_GROUPS.map((group) => (
          <fieldset key={group.id} className="glass-card p-4">
            <legend className="sr-only">{group.name}</legend>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-2">{group.name}</div>
            <ul className="space-y-1">
              {group.features.map((f) => {
                const isOwned = ownedSet.has(f);
                const needs = required.get(f);
                // Held by a selected (or owned) feature that needs it: withDependencies would put it straight back.
                const isRequired = !!needs;
                const checked = selection.all || isOwned || chosen.has(f) || isRequired;
                const locked = disabled || selection.all || isOwned || isRequired;
                return (
                  <li key={f}>
                    <label className={`flex items-start gap-3 rounded-lg p-2 -mx-1 ${locked ? '' : 'cursor-pointer hover:bg-ink-muted/70'}`}>
                      <input type="checkbox" className="sr-only" checked={checked} disabled={locked} onChange={() => toggle(f)} />
                      <span
                        aria-hidden
                        className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                          isOwned ? 'bg-status-running border-status-running text-white' : checked ? 'bg-brand-primary border-brand-primary text-white' : 'border-glass-border-bright bg-white'
                        }`}
                      >
                        {isOwned ? <Lock size={11} /> : checked && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-semibold">{FEATURE_METADATA[f].name}</span>
                          <span className={`text-sm tabular-nums whitespace-nowrap ${isOwned ? 'text-status-running font-semibold' : 'text-text-secondary'}`}>
                            {isOwned ? 'Owned' : formatUsd(LICENSE_FEATURE_PRICES_USD[f])}
                          </span>
                        </span>
                        <span className="block text-xs text-text-muted leading-snug mt-0.5">{FEATURE_METADATA[f].description}</span>
                        {isRequired && !isOwned && !selection.all && (
                          <span className="block text-[11px] text-brand-primary mt-0.5">Required by {needs!.map((n) => FEATURE_METADATA[n].name).join(', ')}</span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))}
      </div>

      <div className="glass-card p-4 flex items-start gap-3">
        <Check size={16} className="text-status-running mt-0.5 shrink-0" />
        <div className="text-sm text-text-secondary">
          <b className="text-text-primary">Included with every appliance:</b> every PLC, CNC and robot driver, unlimited devices and tags, dashboards, historian, scripts, workflows, custom pages, data
          tables, users and backups — plus {LICENSE_BASE_FEATURES.map((f) => (f === 'mesh_networking' ? `${FEATURE_METADATA[f].name} (paired appliances)` : FEATURE_METADATA[f].name)).join(', ')}.
        </div>
      </div>
    </div>
  );
}

/** Scada.Ink (features or All) vs the flat HMI.ink package. */
function PackagePicker({ selection, onChange, disabled }: { selection: LicenseSelection; onChange: (next: LicenseSelection) => void; disabled?: boolean }) {
  const options: Array<{ edition: LicenseEdition; icon: typeof Layers; title: string; body: string; price: string }> = [
    { edition: 'scada', icon: Layers, title: 'Scada.Ink', body: 'Choose features, or get All — historian, alarms, analytics, MES and more.', price: `All ${formatUsd(ALL_LICENSE_PRICE_USD)} · or per feature` },
    { edition: 'hmi', icon: Monitor, title: 'HMI.ink', body: 'Connect, browse tags, dashboards, controls and custom screens.', price: `${formatUsd(HMI_LICENSE_PRICE_USD)} per appliance` },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Package">
      {options.map((o) => {
        const active = selection.edition === o.edition;
        return (
          <button
            key={o.edition}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange({ ...selection, edition: o.edition })}
            className={`flex items-start gap-3 rounded-[var(--radius-glass-sm)] border p-4 text-left transition-all ${
              active ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-glass-border bg-white hover:border-glass-border-bright'
            }`}
          >
            <o.icon size={20} className={active ? 'text-brand-primary mt-0.5' : 'text-text-muted mt-0.5'} />
            <span className="flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-bold">{o.title}</span>
                <span className="text-xs text-text-secondary whitespace-nowrap">{o.price}</span>
              </span>
              <span className="block text-xs text-text-secondary mt-0.5">{o.body}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** The running total. `quote` may be a local preview or the cloud's authoritative quote. */
export function QuoteSummary({
  quote,
  selection,
  onMaintenanceChange,
  maintenanceActive = false,
  pending = false,
  baseUsd = 0,
  children,
}: {
  quote: LicenseQuote;
  /**
   * A new system's appliance and core platform, shown as the first line and
   * added to the total. Display only: online checkout is always for an
   * appliance that already exists, so the cloud's quote never includes it.
   */
  baseUsd?: number;
  selection: LicenseSelection;
  onMaintenanceChange: (on: boolean) => void;
  maintenanceActive?: boolean;
  pending?: boolean;
  children?: React.ReactNode;
}) {
  // What "All" would cost with the same owned features and discount — to nudge when it is cheaper.
  const allQuote = useMemo(
    () => quoteLicense({ features: [], all: true, owned: quote.ownedFeatures, discountPct: quote.discountPct, maintenance: false }),
    [quote.ownedFeatures, quote.discountPct],
  );
  const cheaperAll = quote.edition === 'scada' && !selection.all && !quote.isAll && !quote.empty && allQuote.totalUsd < quote.totalUsd;
  const hmi = quote.edition === 'hmi';
  const base = baseUsd > 0 && !quote.blockedReason ? baseUsd : 0;
  const licenseUsd = quote.empty ? 0 : quote.totalUsd;
  const firstYearMaintenance = quote.empty ? 0 : quote.maintenanceYearlyUsd;
  const dueUsd = Math.round((base + licenseUsd + firstYearMaintenance) * 100) / 100;

  return (
    <div className={`glass-panel p-6 transition-opacity ${pending ? 'opacity-70' : ''}`}>
      <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-text-muted">{base ? 'Your system' : 'Your license'}</h2>

      {base > 0 && (
        <div className="mt-4 flex justify-between gap-3 text-sm">
          <span className="text-text-secondary">Scada.Ink appliance + core platform</span>
          <span className="tabular-nums whitespace-nowrap font-medium">{formatUsd(base)}</span>
        </div>
      )}
      {quote.blockedReason ? (
        <p className="mt-4 text-sm text-status-idle">{quote.blockedReason}</p>
      ) : quote.empty && base ? null : quote.empty ? (
        <p className="mt-4 text-sm text-text-secondary">
          {quote.ownedFeatures.length ? 'This appliance already has everything selected. Pick more features to upgrade.' : 'Choose All features, a starting point, or individual features.'}
        </p>
      ) : (
        <ul className={`${base ? 'mt-2' : 'mt-4'} space-y-2 text-sm max-h-72 overflow-y-auto pr-1`}>
          {quote.lines.map((l, i) => (
            <li key={`${l.kind}-${l.feature ?? i}`} className="flex justify-between gap-3">
              <span className={l.amountUsd < 0 ? 'text-status-running' : 'text-text-secondary'}>{l.label}</span>
              <span className={`tabular-nums whitespace-nowrap ${l.amountUsd < 0 ? 'text-status-running' : 'font-medium'}`}>
                {l.amountUsd < 0 ? `−${formatUsd(-l.amountUsd)}` : formatUsd(l.amountUsd)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Maintenance, when newly added, is billed for its first year at checkout. */}
      {(firstYearMaintenance > 0 || (base > 0 && licenseUsd > 0)) && (
        <div className="mt-4 pt-3 border-t border-glass-border space-y-1.5 text-sm">
          {base > 0 && (
            <div className="flex justify-between gap-3">
              <span className="text-text-secondary">Appliance + core platform</span>
              <span className="tabular-nums">{formatUsd(base)}</span>
            </div>
          )}
          {licenseUsd > 0 && (
            <div className="flex justify-between gap-3">
              <span className="text-text-secondary">{base ? 'Licensed features (one-time)' : 'License (one-time)'}</span>
              <span className="tabular-nums">{formatUsd(licenseUsd)}</span>
            </div>
          )}
          {firstYearMaintenance > 0 && (
            <div className="flex justify-between gap-3">
              <span className="text-text-secondary">{quote.edition === 'hmi' ? 'Support, first year' : 'Maintenance, first year'}</span>
              <span className="tabular-nums">{formatUsd(firstYearMaintenance)}</span>
            </div>
          )}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-glass-border flex items-baseline justify-between">
        <span className="text-sm font-semibold">{base ? 'System total' : 'Due today'}</span>
        <span className="text-3xl font-extrabold tabular-nums">{formatUsd(dueUsd)}</span>
      </div>
      <div className="text-xs text-text-muted text-right">
        {quote.maintenanceYearlyUsd > 0 && !quote.empty
          ? `Perpetual license · ${quote.edition === 'hmi' ? 'support' : 'maintenance'} renews at ${formatUsd(quote.maintenanceYearlyUsd)}/yr · USD`
          : 'One-time · perpetual license · USD'}
      </div>

      {cheaperAll && (
        <div className="mt-4 rounded-[var(--radius-glass-sm)] border border-brand-secondary/30 bg-brand-secondary/5 px-3.5 py-3 text-sm">
          <div className="flex items-center gap-1.5 font-semibold text-brand-secondary">
            <Sparkles size={14} /> All features costs less
          </div>
          <p className="text-text-secondary mt-0.5">
            Get every feature for {formatUsd(allQuote.totalUsd)} — {formatUsd(quote.totalUsd - allQuote.totalUsd)} less than this selection.
          </p>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-glass-border">
        {maintenanceActive ? (
          <div className="flex items-start gap-2.5 text-sm text-text-secondary">
            <Wrench size={15} className="text-status-running mt-0.5 shrink-0" />
            <span>
              Maintenance is active on this appliance and will cover the upgraded license
              {quote.empty ? '.' : `, renewing at ${formatUsd(quoteMaintenancePreview(quote))}/yr.`}
            </span>
          </div>
        ) : (
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={selection.maintenance} onChange={(e) => onMaintenanceChange(e.target.checked)} className="mt-1 accent-[#3b82f6]" />
            <span className="flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">{hmi ? 'Support & updates' : 'Maintenance & support'}</span>
                <span className="text-sm tabular-nums">{quote.empty ? '—' : `${formatUsd(quote.maintenanceYearlyUsd || quoteMaintenancePreview(quote))}/yr`}</span>
              </span>
              <span className="block text-xs text-text-muted mt-0.5">
                {hmi
                  ? `Priority support and software updates, ${formatUsd(HMI_SUPPORT_YEARLY_USD)} per year. Optional — the license keeps working without it.`
                  : `Software updates and priority support, ${Math.round(MAINTENANCE_RATE * 100)}% of the license value per year. Optional — the license keeps working without it.`}
              </span>
            </span>
          </label>
        )}
      </div>

      {quote.discountPct > 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-status-running">
          <Check size={13} /> Certified partner pricing: {quote.discountPct}% off applied
        </div>
      )}

      {children && <div className="mt-5">{children}</div>}

      <p className="mt-4 flex items-start gap-2 text-[11px] text-text-muted">
        <Info size={12} className="mt-0.5 shrink-0" />
        Each license key is locked to one appliance’s hardware ID and can’t be moved to another appliance.
      </p>
    </div>
  );
}

/** Maintenance price to show beside the unchecked box. */
function quoteMaintenancePreview(q: LicenseQuote): number {
  if (q.edition === 'hmi') return Math.round(HMI_SUPPORT_YEARLY_USD * (1 - q.discountPct / 100) * 100) / 100;
  const value = q.isAll ? ALL_LICENSE_PRICE_USD : [...q.newFeatures, ...q.ownedFeatures].reduce((s, f) => s + LICENSE_FEATURE_PRICES_USD[f], 0);
  return Math.round(value * MAINTENANCE_RATE * (1 - q.discountPct / 100) * 100) / 100;
}
